import React from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  VStack,
  HStack,
  Icon,
  Badge,
  SimpleGrid,
  Circle,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { BarChart2, TrendingUp, TrendingDown, Clock, Activity, Brain, Heart, Coffee, type LucideIcon } from 'lucide-react';

interface MetricType {
  label: string;
  value: number;
  goal: number;
  icon: LucideIcon;
  color: string;
  progress: number;
}

interface MobileResilienceMetricsProps {
  metrics: MetricType[];
  score: number;
  trend: string;
  onNavigate: (tab: string) => void;
}

const MobileResilienceMetrics: React.FC<MobileResilienceMetricsProps> = ({ metrics, score, trend, onNavigate }) => {
  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  const methodistBlue = '#006BA6';
  
  // Weekly data for charts (simplified representation)
  const weeklyData = [
    { day: 'Mon', value: 55 },
    { day: 'Tue', value: 62 },
    { day: 'Wed', value: 58 },
    { day: 'Thu', value: 65 },
    { day: 'Fri', value: 70 },
    { day: 'Sat', value: 75 },
    { day: 'Sun', value: 78 },
  ];
  
  return (
    <Box p={4} pt={10}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <VStack align="flex-start" spacing={0}>
          <Heading size="sm" color={methodistBlue}>Your Metrics</Heading>
          <Text fontSize="xs" color={secondaryTextColor}>Wellness tracking</Text>
        </VStack>
        <Badge colorScheme="blue" variant="subtle" fontSize="xs">
          <Icon as={Clock} boxSize={3} mr={1} />
          Updated 2h ago
        </Badge>
      </Flex>
      
      {/* Metrics Overview */}
      <SimpleGrid 
        columns={2} 
        spacing={3} 
        mb={6}
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 } as any}
      >
        {metrics.map((metric, index) => (
          <Box
            key={index}
            bg={cardBg}
            p={3}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
          >
            <HStack spacing={2} mb={2}>
              <Circle size="30px" bg={`${metric.color}.100`} color={`${metric.color}.500`}>
                <Icon as={metric.icon} boxSize={4} />
              </Circle>
              <Text fontSize="sm" fontWeight="medium" isTruncated>
                {metric.label}
              </Text>
            </HStack>
            
            <Flex align="baseline" mb={1}>
              <Text fontSize="xl" fontWeight="bold" mr={2}>
                {metric.value}%
              </Text>
              <HStack spacing={1} color={trend === 'up' ? 'green.500' : 'red.500'}>
                <Icon 
                  as={trend === 'up' ? TrendingUp : TrendingDown} 
                  boxSize={3} 
                />
                <Text fontSize="xs" fontWeight="medium">
                  {trend === 'up' ? '+' : '-'}{Math.abs(metric.progress)}%
                </Text>
              </HStack>
            </Flex>
            
            <Text fontSize="xs" color={secondaryTextColor} noOfLines={2}>
              Your {metric.label} is {metric.value}%. You are {Math.abs(metric.progress)}% {trend === 'up' ? 'above' : 'below'} your goal.
            </Text>
          </Box>
        ))}
      </SimpleGrid>
      
      {/* Weekly Trend */}
      <Text fontSize="sm" fontWeight="medium" mb={3}>Weekly Wellness Trend</Text>
      <Box
        bg={cardBg}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        mb={6}
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 } as any}
      >
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontSize="sm" fontWeight="medium">Adaptability Score</Text>
          <HStack spacing={1}>
            <Icon as={TrendingUp} color="green.500" boxSize={3} />
            <Text fontSize="xs" color="green.500">Improving</Text>
          </HStack>
        </Flex>
        
        {/* Simplified chart visualization */}
        <Box h="120px" position="relative">
          <Flex h="100%" align="flex-end" justify="space-between">
            {weeklyData.map((day, i) => (
              <VStack key={i} spacing={1} flex="1">
                <Box 
                  w="80%" 
                  h={`${day.value}%`} 
                  bg={`blue.${Math.floor(day.value / 10) * 100}`} 
                  borderRadius="sm"
                  transition="height 0.3s"
                />
                <Text fontSize="xs" color={secondaryTextColor}>
                  {day.day}
                </Text>
              </VStack>
            ))}
          </Flex>
          
          {/* Horizontal grid lines */}
          {[25, 50, 75].map((line) => (
            <Box 
              key={line}
              position="absolute"
              left={0}
              right={0}
              top={`${100 - line}%`}
              h="1px"
              bg="gray.200"
              zIndex={0}
            />
          ))}
        </Box>
      </Box>
      
      {/* Insights */}
      <Text fontSize="sm" fontWeight="medium" mb={3}>Personalized Insights</Text>
      <Box
        bg={cardBg}
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={borderColor}
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 } as any}
      >
        <HStack spacing={3} mb={3}>
          <Icon as={BarChart2} boxSize={5} color={methodistBlue} />
          <Text fontSize="sm" fontWeight="medium">Your Wellness Analysis</Text>
        </HStack>
        
        <VStack align="start" spacing={3}>
          <Text fontSize="xs">
            Your sleep quality has improved significantly, which is positively impacting your overall resilience score. Continue with your current sleep routine.
          </Text>
          
          <Text fontSize="xs">
            Physical activity levels are below your target. Consider scheduling short walking breaks between patient appointments to improve this metric.
          </Text>
          
          <Text fontSize="xs">
            Your stress levels tend to peak on Thursdays. We recommend scheduling a mindfulness session on Wednesday evenings to prepare.
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default MobileResilienceMetrics;
